import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPin } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useHomeFeeds } from "@/context/HomeFeedsContext";
import { useBatchTranslate } from "@/lib/useBatchTranslate";
import { HomeSection } from "@/components/home/HomeSection";
import { HomeDynamicCarousel } from "@/components/home/HomeDynamicCarousel";
import { ListingCard } from "@/components/ListingCard";
import { PopularListingCard } from "@/components/home/PopularListingCard";
import { SupplierHomeCard } from "@/components/home/SupplierHomeCard";
import { Button } from "@/components/ui/button";

const HomeNearYouMapInner = lazy(() =>
  import("@/components/home/HomeNearYouMapInner").then((m) => ({ default: m.HomeNearYouMapInner })),
);

/**
 * Single proof block: live listings/suppliers + map.
 * Replaces Featured / TopRated / Nearby / Popular / Map on the homepage.
 */
export function HomeProofSection() {
  const { t } = useLang();
  const { data, loading, coords } = useHomeFeeds();

  const nearby = data?.nearby_listings?.items || [];
  const popular = data?.popular_listings || [];
  const featured = data?.featured_suppliers || [];
  const geo = data?.nearby_listings?.geo;

  const listingMode = nearby.length > 0 ? "nearby" : popular.length > 0 ? "popular" : null;
  const listingItems = listingMode === "nearby" ? nearby : popular;
  const titleMap = useBatchTranslate(listingMode === "nearby" ? listingItems : []);

  const showSuppliersFallback = !loading && listingItems.length === 0 && featured.length > 0;
  const mapData = data?.nearby_map;
  const hasMarkers =
    (mapData?.suppliers?.length || 0) + (mapData?.listings?.length || 0) > 0;

  const subtitle = geo
    ? t("home.proof_subtitle_geo")
    : t("home.proof_subtitle");

  const hasAnyContent = loading || listingItems.length > 0 || featured.length > 0 || hasMarkers;
  if (!hasAnyContent) return null;

  return (
    <HomeSection tone="muted" testId="home-proof" id="home-near-you-map" className="scroll-mt-24">
      {listingItems.length > 0 || loading ? (
        <HomeDynamicCarousel
          kicker={t("home.proof_kicker")}
          title={t("home.proof_title")}
          subtitle={subtitle}
          linkTo={listingMode === "popular" ? "/recherche?sort=popular" : "/recherche"}
          linkLabel={t("home.see_all_listings")}
          loading={loading}
          items={listingItems}
          renderItem={(listing) =>
            listingMode === "nearby" ? (
              <ListingCard
                listing={listing}
                testId={`home-proof-${listing.id}`}
                overrideTitle={titleMap[listing.id]}
              />
            ) : (
              <PopularListingCard listing={listing} testId={`home-proof-${listing.id}`} />
            )
          }
        />
      ) : showSuppliersFallback ? (
        <HomeDynamicCarousel
          kicker={t("home.proof_kicker")}
          title={t("home.proof_title")}
          subtitle={t("home.proof_subtitle_suppliers")}
          linkTo="/fournisseurs"
          linkLabel={t("home.find_suppliers")}
          loading={false}
          items={featured.slice(0, 8)}
          renderItem={(supplier) => (
            <SupplierHomeCard supplier={supplier} testId={`home-proof-supplier-${supplier.id}`} />
          )}
        />
      ) : (
        <div className="mb-8 text-center sm:mb-10 sm:text-left">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-matpro-green">
            {t("home.proof_kicker")}
          </p>
          <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-matpro-anthracite sm:text-3xl">
            {t("home.proof_title")}
          </h2>
          <p className="mt-2 text-sm text-matpro-anthracite-muted sm:text-base">
            {subtitle}
          </p>
        </div>
      )}

      <div className="mt-8 overflow-hidden rounded-2xl border border-matpro-border bg-white shadow-[0_8px_32px_rgba(24,24,27,0.06)] sm:mt-10">
        {loading ? (
          <div className="flex h-[280px] items-center justify-center bg-matpro-section sm:h-[320px]">
            <Loader2 className="h-8 w-8 animate-spin text-matpro-green" />
          </div>
        ) : hasMarkers ? (
          <Suspense
            fallback={
              <div className="flex h-[280px] items-center justify-center bg-matpro-section sm:h-[320px]">
                <Loader2 className="h-8 w-8 animate-spin text-matpro-green" />
              </div>
            }
          >
            <HomeNearYouMapInner mapData={mapData} userLocation={coords} />
          </Suspense>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
            <MapPin className="h-7 w-7 text-matpro-green" />
            <p className="max-w-md text-sm text-matpro-anthracite-muted">{t("home.near_map_empty")}</p>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap justify-center gap-3 sm:justify-start">
        <Button asChild variant="outline" className="rounded-xl border-matpro-border font-semibold">
          <Link to="/fournisseurs">{t("home.find_suppliers")}</Link>
        </Button>
        <Button asChild className="rounded-xl bg-matpro-green font-semibold text-white hover:bg-matpro-green-dark">
          <Link to="/recherche">{t("home.see_all_listings")}</Link>
        </Button>
      </div>
    </HomeSection>
  );
}
